import { UUID } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { limitConcurrency } from '@/utils/limitConcurrency';
import { useQuery } from '@tanstack/react-query';

export const GET_ASSET_THUMBNAIL_QUERY_KEY = 'assets.getAssetThumbnail';

// Views that mount hundreds of thumbnails at once (report groups renders three per
// group with no upper bound) would otherwise fire every request in the same tick and
// saturate the API, which then times out at the load balancer for every other caller
// on the stack. One shared queue across all thumbnail requests keeps a large view
// loading steadily instead of all at once.
const MAX_CONCURRENT_THUMBNAIL_REQUESTS = 6;
const limitThumbnailRequests = limitConcurrency(MAX_CONCURRENT_THUMBNAIL_REQUESTS);

// Each cached thumbnail holds a decoded blob until the query cache drops it and
// installObjectUrlRevoker revokes the object URL. Holding scrolled-past thumbnails for an
// hour is what let a large report accumulate a gigabyte of images.
const UNMOUNTED_THUMBNAIL_RETENTION_MS = 5 * 60 * 1000;

export const useGetAssetThumbnail = ({
  assetId,
  assetTenantId,
  version,
  enabled = true,
}: {
  assetId: UUID;
  assetTenantId?: UUID;
  version?: string;
  enabled?: boolean;
}) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const {
    data: thumbnail,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GET_ASSET_THUMBNAIL_QUERY_KEY, { assetId, version }],
    // Reading the signal is what makes React Query cancel this query once its last
    // subscriber unmounts. A tile that scrolls out of a virtualized list then leaves the
    // shared queue instead of draining one of its six slots for something nobody can see.
    queryFn: ({ signal }) =>
      limitThumbnailRequests(async () => {
        const effectiveTenantId = assetTenantId ?? tenantId;
        const { data, status, headers } = await api.http.request<Blob>({
          path: `/tenants/${effectiveTenantId}/assets/${assetId}/thumbnail.png`,
          method: 'GET',
          secure: true,
          format: 'blob',
          query: version ? { v: version } : undefined,
          signal,
        });

        if (status !== 200) {
          throw new Error('Failed to download thumbnail');
        }

        const contentType = headers['content-type'];
        const objectUrl = URL.createObjectURL(
          new Blob([data], { type: typeof contentType === 'string' ? contentType : undefined })
        );

        // A response that lands while the query is being cancelled never reaches the
        // cache, so installObjectUrlRevoker never sees it and the blob would leak.
        if (signal.aborted) {
          URL.revokeObjectURL(objectUrl);
          throw signal.reason;
        }

        return objectUrl;
      }, signal),
    enabled: enabled && !!assetId && !!tenantId,
    refetchOnMount: false,
    retry: false,
    // retry: false only covers the first attempt. retryOnMount defaults to true, so every remount of
    // a failed thumbnail asks again -- and the hover preview mounts a second one over every row.
    // An asset without a thumbnail does not grow one between hovers.
    retryOnMount: false,
    staleTime: 60 * 60 * 1000,
    gcTime: UNMOUNTED_THUMBNAIL_RETENTION_MS,
  });

  return { thumbnail, isLoading, error };
};
